import { useRef, useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { PILL, TH } from '@/features/fraud/format'
import { discountErrorMessage } from '@/lib/api/discount-client'
import { useArchiveDiscountSchedule, useUpdateDiscountSchedule } from '@/lib/api/discount-queries'
import type { DiscountCreatedScope, DiscountSchedule } from '@/lib/api/discount-types'
import { cn } from '@/lib/utils'
import {
  STATUS_LABEL,
  ruleSummary,
  scheduleStatus,
  statusDetail,
  targetsSummary,
  windowSummary,
  type PricingEvent,
  type ScheduleStatus,
  type StatusEvent,
} from './list-format'

// Each row is its own grid, so every track is either a fraction or fixed:
// an `auto` track would size per row and the columns would drift apart.
// Below xl a row stacks as a card instead. The actions column is wide when
// some row has Edit, Pause and Archive, and narrow when every row only has View.
const GRID_BASE = 'xl:grid xl:items-start xl:gap-x-4'
const GRID_MANAGE = 'xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.5fr)_minmax(0,1.1fr)_16rem]'
const GRID_VIEW_ONLY = 'xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.5fr)_minmax(0,1.1fr)_5rem]'

// Keeps a pending button focusable (a disabled one drops keyboard focus); its handler ignores clicks.
const PENDING = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50'

const STATUS_TONE: Record<ScheduleStatus, string> = {
  running: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
  not_charging: 'border border-amber-300 text-amber-800 dark:border-amber-700 dark:text-amber-300',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
  ended: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  archived: 'border border-neutral-200 text-neutral-500 dark:border-neutral-700 dark:text-neutral-400',
}

const SCOPE_META: Record<DiscountCreatedScope, { label: string; title: string }> = {
  event: { label: 'Event', title: 'Created by an event admin' },
  vendor: { label: 'Vendor', title: 'Created by a vendor admin' },
  location: { label: 'Location', title: 'Created by a location admin' },
}

/** Column titles for the xl layout; below xl each row labels its own facts. */
export function ScheduleListHeader({ manageable, timeZoneLabel }: { manageable: boolean; timeZoneLabel: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('hidden border-b border-neutral-200 dark:border-neutral-800 px-1', GRID_BASE, manageable ? GRID_MANAGE : GRID_VIEW_ONLY)}
    >
      <div className={TH}>Promotion</div>
      <div className={TH}>Status</div>
      <div className={TH}>Discount</div>
      <div className={TH}>When <span className="normal-case tracking-normal font-normal">({timeZoneLabel})</span></div>
      <div className={TH}>Applies to</div>
      <div className={TH} />
    </div>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 text-sm text-neutral-700 dark:text-neutral-300">
      <div className="mb-0.5 text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400 xl:sr-only">{label}</div>
      {children}
    </div>
  )
}

export function ScheduleRow({
  eventId, schedule, event, timeZone, now, manageable, canManage, promotionsOff, onOpen, onArchived,
}: {
  eventId: number
  schedule: DiscountSchedule
  event: PricingEvent & StatusEvent
  timeZone: string
  now: Date
  /** Some row in the list has Edit, Pause and Archive (the wide actions column). */
  manageable: boolean
  /** The viewer may edit, pause and archive this row (the schedule's `can_edit`); otherwise it offers View. */
  canManage: boolean
  /** The event's promotions switch is off: nothing is discounted whatever the status says. */
  promotionsOff: boolean
  /** Opens the editor: to edit when `canManage`, read-only otherwise. */
  onOpen: (schedule: DiscountSchedule) => void
  /** The row archived its schedule (it may leave the list, taking focus with it). */
  onArchived: (schedule: DiscountSchedule) => void
}) {
  const update = useUpdateDiscountSchedule(eventId)
  const archive = useArchiveDiscountSchedule(eventId)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const archiveTrigger = useRef<HTMLButtonElement>(null)
  const busy = update.isPending || archive.isPending

  const info = scheduleStatus(schedule, timeZone, now, event)
  const detail = statusDetail(info, schedule, timeZone, now)
  const rule = ruleSummary(schedule, event)
  const name = schedule.name || `Promotion #${schedule.id}`
  const scope = SCOPE_META[schedule.created_scope] ?? { label: schedule.created_scope, title: '' }
  const showActions = canManage && !schedule.archived_at
  const offNote = promotionsOff && (info.status === 'not_charging' || info.status === 'scheduled')
    ? 'Promotions are switched off for this event, so nothing is discounted until they are turned on.'
    : undefined

  // The saved schedule goes into the cached lists from the mutation hook.
  const togglePause = () => {
    if (busy) return
    setError(null)
    const resume = !schedule.is_active
    update.mutate(
      { id: schedule.id, data: { is_active: resume } },
      { onError: (err) => setError(discountErrorMessage(err, resume ? 'Could not resume this promotion.' : 'Could not pause this promotion.')) },
    )
  }

  const cancelArchive = () => {
    setConfirmArchive(false)
    archiveTrigger.current?.focus()
  }

  const confirmAndArchive = () => {
    if (busy) return
    setError(null)
    // A promise rather than mutate callbacks: an archived row can leave the
    // list (unmount) before those would run.
    archive.mutateAsync(schedule.id).then(
      () => {
        setConfirmArchive(false)
        onArchived(schedule)
      },
      (err: unknown) => setError(discountErrorMessage(err, 'Could not archive this promotion.')),
    )
  }

  return (
    <li
      aria-busy={busy || undefined}
      className={cn(
        'flex flex-col gap-3 px-4 py-4 xl:gap-y-3 xl:px-1',
        GRID_BASE, manageable ? GRID_MANAGE : GRID_VIEW_ONLY,
      )}
    >
      <div className="flex items-start justify-between gap-3 xl:contents">
        <div className="min-w-0 xl:px-3">
          <div className="font-medium text-neutral-900 dark:text-white break-words">{name}</div>
          <span
            title={scope.title}
            className="mt-1 inline-block rounded border border-neutral-200 px-1.5 text-[11px] leading-4 uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:text-neutral-400"
          >
            {scope.label}
          </span>
        </div>
        <div className="shrink-0 text-right xl:px-3 xl:text-left">
          <span className={cn(PILL, STATUS_TONE[info.status])} title={offNote}>
            {STATUS_LABEL[info.status]}
          </span>
          {detail && <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{detail}</div>}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 xl:contents">
        <div className="xl:px-3">
          <Fact label="Discount">
            <div className="font-medium text-neutral-900 dark:text-white">{rule.main}</div>
            {rule.note && <div className="text-xs text-neutral-500 dark:text-neutral-400">{rule.note}</div>}
          </Fact>
        </div>
        <div className="xl:px-3">
          <Fact label="When">{windowSummary(schedule, timeZone, now)}</Fact>
        </div>
        <div className="xl:px-3">
          <Fact label="Applies to">{targetsSummary(schedule)}</Fact>
        </div>
      </div>

      {showActions ? (
        <div className="flex flex-wrap gap-2 xl:justify-end xl:px-3">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => onOpen(schedule)}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-disabled={busy || undefined}
            onClick={togglePause}
            className={PENDING}
          >
            {update.isPending ? (schedule.is_active ? 'Pausing…' : 'Resuming…') : schedule.is_active ? 'Pause' : 'Resume'}
          </Button>
          <Button
            ref={archiveTrigger}
            variant="ghost"
            size="sm"
            // Stays enabled while the confirm strip is open, so Cancel can hand focus back to it.
            disabled={busy && !confirmArchive}
            aria-expanded={confirmArchive}
            onClick={() => { setError(null); setConfirmArchive(true) }}
            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            Archive
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 xl:justify-end xl:px-3">
          <Button variant="outline" size="sm" onClick={() => onOpen(schedule)}>
            View
          </Button>
        </div>
      )}

      {showActions && confirmArchive && (
        <div
          role="group"
          aria-label={`Archive ${name}?`}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !busy) cancelArchive()
          }}
          className="xl:col-span-full xl:mx-3 flex flex-wrap items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-200"
        >
          <span className="mr-auto">
            Archive <span className="font-medium">{name}</span>? It stops discounting now, and an archived promotion can't be edited again.
          </span>
          <Button variant="destructive" size="sm" aria-disabled={busy || undefined} onClick={confirmAndArchive} className={PENDING}>
            {archive.isPending ? 'Archiving…' : 'Archive'}
          </Button>
          <Button variant="ghost" size="sm" autoFocus disabled={busy} onClick={cancelArchive}>
            Cancel
          </Button>
        </div>
      )}

      {error && (
        <p role="alert" className="xl:col-span-full xl:px-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </li>
  )
}
